import React, { useContext } from 'react'
import { useForm } from 'react-hook-form'
import TextInput from 'components/generic/Input/TextInput'
import { trackClickEvent } from 'utils/Analytics'
import { StepHeading, Form } from 'components/GetStarted/styles'
import InputErrorMessage from 'components/generic/Input/InputErrorMessage'
import CREATE_USER_MUTATION from 'components/api/mutations/createUser.gql'
import { useMutation } from '@apollo/client'
import { LegalText } from 'components/GetStarted/StepUserCreate/styles'
import { DEFAULT_USER_INPUT } from '../constants'
import { AppContext } from 'contexts/AppContext'
import { getUserTimezone } from 'utils/getStartedUtils'
import LoadingIndicatorButton from 'components/generic/LoadingIndicatorButton'
import FormSubmissionError from 'components/generic/FormSubmissionError'
import getSentry from 'utils/Sentry'

const { captureException } = getSentry()

const StepUserCreate = ({ onStepComplete, userInput, refetchData }) => {
  const { domain } = useContext(AppContext)
  const { register, handleSubmit, errors, formState } = useForm({
    mode: 'all',
  })
  const { isValid, isSubmitting } = formState
  const passwordValidationRules = {
    required: 'Please enter a password.',
    minLength: {
      value: 6,
      message: 'Please enter at least 6 characters',
    },
  }
  const [createUser, { error }] = useMutation(CREATE_USER_MUTATION)
  const onSubmit = async ({ password }) => {
    try {
      const input = Object.keys(DEFAULT_USER_INPUT).reduce(
        (acc, key) => ({
          ...acc,
          [key]: userInput[key],
        }),
        {}
      )
      await createUser({
        variables: { input: { ...input, time_zone: getUserTimezone(), password } },
      })
      await refetchData()
      onStepComplete()
    } catch (err) {
      captureException(err)
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <StepHeading>{`Almost there, ${userInput?.first_name || ''}`}</StepHeading>
      <FormSubmissionError isVisible={error} heading='We were unable to create your account.'>
        Please try again or contact <a href='mailto:info@careguide.com'>info@careguide.com</a> for
        assistance.
      </FormSubmissionError>
      <TextInput
        id='get-started-password'
        autoFocus={true}
        name='password'
        type='password'
        label='Password (Min 6 characters)'
        disabled={isSubmitting}
        isValid={!errors.password}
        ref={register(passwordValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='get-started-password' name='password' />
      <LegalText>
        By clicking next, you agree to the{' '}
        <a href='/terms-of-service' target='_blank'>
          Terms of Service
        </a>
        ,{' '}
        <a href='/privacy' target='_blank'>
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href='/cancellation-policy' target='_blank'>
          Cancellation Policy
        </a>
      </LegalText>
      <LoadingIndicatorButton
        mt='2rem'
        disabled={!isValid}
        onClick={() => trackClickEvent(domain, 'get-started-next-user-create')}
        isLoading={isSubmitting}
      >
        {isSubmitting ? 'Creating your account' : 'Next'}
      </LoadingIndicatorButton>
    </Form>
  )
}

export default StepUserCreate
